import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { changePassword, completeNewPassword, setAuthError } from '../store/slices/authSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MyTextField from '../components/MyTextField';
import MySnackbar from '../components/MySnackbar';
import LoadingComponent from '../components/LoadingComponent';
import s10Logo from '../assets/images/s10Logo.png';

const ChangePassword = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error, challengeName } = useSelector((state: RootState) => state.auth);
    
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword] = useState(false);
    const [oldPasswordError, setOldPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');

    const isRequired = location.state?.required || challengeName === 'NEW_PASSWORD_REQUIRED';

    useEffect(() => {
        if (!isRequired) {
        }
    }, [isRequired]);

    const validateForm = (): boolean => {
        let isValid = true;

        if (!isRequired) {
            if (!oldPassword) {
                setOldPasswordError('La contraseña actual es requerida');
                isValid = false;
            } else {
                setOldPasswordError('');
            }
        }

        if (!newPassword) {
            setNewPasswordError('La nueva contraseña es requerida');
            isValid = false;
        } else if (newPassword.length < 6) {
            setNewPasswordError('La contraseña debe tener al menos 6 caracteres');
            isValid = false;
        } else {
            setNewPasswordError('');
        }

        if (newPassword !== confirmPassword) {
            setConfirmError('Las contraseñas no coinciden');
            isValid = false;
        } else {
            setConfirmError('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        let result;
        
        if (isRequired) {
            result = await dispatch(completeNewPassword({
                newPassword,
                userAttributes: {}
            }));
            
            if (completeNewPassword.fulfilled.match(result)) {
                navigate('/dashboard');
            }
        } else {
            result = await dispatch(changePassword({
                oldPassword,
                newPassword
            }));
            
            if (changePassword.fulfilled.match(result)) {
                navigate('/dashboard', { state: { message: 'Contraseña cambiada exitosamente' } });
            }
        }
    };

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh', backgroundColor: theme.s10palette?.S10Blueblue50 }}>
        <MySnackbar
            open={!!error}
            autoHideDuration={6000}
            msg={error || ''}
            severity="error"
            onClose={() => dispatch(setAuthError(''))}
        />

        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
            <Grid container direction="column" alignItems="center" component="form" onSubmit={handleSubmit} spacing={2}>
            <Grid item>
                <img src={s10Logo} alt="S10" style={{ width: '8.75rem' }} />
            </Grid>

            <Typography variant="h5" align="center" gutterBottom>
                {isRequired ? 'Cambio de contraseña requerido' : 'Cambiar contraseña'}
            </Typography>

            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 2 }}>
                {isRequired 
                ? 'Por seguridad, debes cambiar tu contraseña antes de continuar.'
                : 'Ingresa tu contraseña actual y la nueva contraseña.'}
            </Typography>

            <Divider sx={{ width: '100%', my: 2 }} />

            {!isRequired && (
                <MyTextField
                    label="Contraseña actual"
                    type={showPassword ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)}
                    error={!!oldPasswordError}
                    helperText={oldPasswordError}
                    fullWidth
                />
            )}

            <MyTextField
                label="Nueva contraseña"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                error={!!newPasswordError}
                helperText={newPasswordError}
                fullWidth
            />

            <MyTextField
                label="Confirmar nueva contraseña"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                error={!!confirmError}
                helperText={confirmError}
                fullWidth
            />

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        sx={{ borderColor: theme.s10palette?.S10Greygrey300 }}
                    >
                        Cancelar
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isLoading}
                        sx={{
                        backgroundColor: theme.s10palette?.S10Blueblue600,
                        '&:hover': { backgroundColor: theme.s10palette?.S10Blueblue700 },
                        }}
                    >
                        {isRequired ? 'Continuar' : 'Cambiar'}
                    </Button>
                </Grid>
            </Grid>
            </Grid>
        </Paper>

        {isLoading && <LoadingComponent loadingText="Procesando..." />}
    </Grid>
  );
};

export default ChangePassword;